import FullDialog from '@map/components/layout/FullDialog';
import StaticPageContent from '@map/components/StaticPageContent';
import { buildApiUrl } from '@map/hooks/baseApi';
import { Tab, Tabs } from '@mui/material';
import { useCurrentLocale } from '@shared/hooks/intl';
import { makeStyles } from '@skill-survey/theme';
import PageSlide from '@ui/PageSlide';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type TabName = 'termsOfUse' | 'privacyPolicy';

const tabToPageMap = {
  termsOfUse: 'terms_of_use',
  privacyPolicy: 'privacy_policy',
};

const useStyles = makeStyles(({ spacing, typography }) => ({
  label: {
    lineHeight: '1.5',
  },
  contentWrapper: {
    padding: spacing(5, 6, 4),
    width: '100%',
    '& h2, & h3': {
      ...typography.subtitle2,
    },
  },
}));

const PrivacyDialog = ({ open, close }: { open: boolean; close: () => void }) => {
  const [tab, setTab] = useState<TabName | null>('termsOfUse');
  const [page, setPage] = useState<string | null>(null);
  const currentLocale = useCurrentLocale();
  const tabEl = useRef<HTMLDivElement | null>(null);
  const { classes } = useStyles();

  useEffect(() => {
    const fetchPageContent = async () => {
      if (tab) {
        try {
          const response = await axios.get<{ data: string }>(
            buildApiUrl(`static_contents/translated/page/${tabToPageMap[tab]}/${currentLocale}`),
          );
          setPage(response.data.data);
        } catch (error) {
          console.error('Failed to fetch page content:', error);
          setPage('');
        }
      }
    };

    fetchPageContent();
  }, [tab, currentLocale]);

  const onTransitionEnd = () => {
    tabEl.current?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
  };

  useEffect(() => {
    onTransitionEnd();
  }, [tab]);

  return (
    <FullDialog
      open={open}
      bgcolor="neutrals.300"
      handleClose={close}
      navigation={
        <Tabs value={tab} variant="fullWidth" onChange={(_, value) => setTab(value)}>
          <Tab
            value="termsOfUse"
            className={classes.label}
            label={<FormattedMessage id="registration.terms_and_conditions" />}
          />
          <Tab
            value="privacyPolicy"
            className={classes.label}
            label={<FormattedMessage id="registration.privacy_policy_tab" />}
          />
        </Tabs>
      }
      showHelp={false}
      slideDirection="left"
    >
      <PageSlide
        key={tab}
        direction={tab === 'termsOfUse' ? 'right' : 'left'}
        transitionProps={{ onEnter: onTransitionEnd }}
      >
        <div ref={tabEl}>
          <StaticPageContent className={classes.contentWrapper} content={page ?? ''} />
        </div>
      </PageSlide>
    </FullDialog>
  );
};

export default PrivacyDialog;
